import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Box, Button, Typography } from "@mui/material";
import { DOCS_URL } from "constants/common";
import logoMark from "images/branding/orkes-logo-mark-purple-2x.png";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { allDocs, docPath } from "./docsManifest";
import { DOCS_BORDER, DOCS_HEADING, DOCS_MUTED, DOCS_TEXT } from "./docsStyles";

const CLOUD_BLUE = "#2f6bec";
const CLOUD_BLUE_DARK = "#2457c9";
const CONDUCTOR_PURPLE = "#6e4ff5";

// Each illustration draws on a fixed canvas: an SVG layer for the connectors, with the icon tiles
// positioned on top of it. The cloud one is wider so its tiles sit well clear of the cloud.
const ART_WIDTH = 320;
const CLOUD_ART_WIDTH = 400;
const ART_HEIGHT = 200;

const DOT_GRID = {
  backgroundImage: "radial-gradient(#c9cfe3 1.5px, transparent 1.5px)",
  backgroundSize: "22px 22px",
};

interface IconTileProps {
  children: ReactNode;
  x: number;
  y: number;
  size?: number;
  color: string;
  filled?: boolean;
}

const IconTile = ({
  children,
  x,
  y,
  size = 56,
  color,
  filled,
}: IconTileProps) => (
  <Box
    sx={{
      position: "absolute",
      left: x,
      top: y,
      width: size,
      height: size,
      borderRadius: filled ? 3 : 2,
      backgroundColor: filled ? color : "#fff",
      color: filled ? "#fff" : color,
      boxShadow: filled
        ? "0 10px 24px rgba(110, 79, 245, 0.35)"
        : "0 4px 14px rgba(40, 60, 140, 0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { fontSize: filled ? 46 : 30 },
    }}
  >
    {children}
  </Box>
);

const ArtCanvas = ({
  children,
  width = ART_WIDTH,
}: {
  children: ReactNode;
  width?: number;
}) => (
  <Box
    aria-hidden
    sx={{
      position: "relative",
      flexShrink: 0,
      width,
      height: ART_HEIGHT,
      // Shrink to fit a phone-width card rather than clipping the outer tiles.
      transform: { xs: "scale(0.8)", sm: "none" },
    }}
  >
    {children}
  </Box>
);

const CloudArt = () => (
  <ArtCanvas width={CLOUD_ART_WIDTH}>
    <svg
      width={CLOUD_ART_WIDTH}
      height={ART_HEIGHT}
      style={{ position: "absolute", inset: 0 }}
    >
      <defs>
        <linearGradient id="docs-cloud-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#e3e8fb" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="#9fb0e0" strokeWidth="1.2" strokeDasharray="3 3">
        <path d="M76 38 H128 V72 H164" />
        <path d="M324 38 H272 V72 H236" />
        <path d="M66 162 H124 V132 H156" />
        <path d="M334 162 H276 V132 H244" />
      </g>
      <g
        fill="url(#docs-cloud-fill)"
        stroke="#ffffff"
        strokeWidth="2"
        style={{ filter: "drop-shadow(0 6px 12px rgba(40, 60, 140, 0.15))" }}
      >
        <path d="M152 150 a30 30 0 0 1 4 -59 a40 40 0 0 1 76 -6 a34 34 0 0 1 18 65 z" />
      </g>
    </svg>
    <Box
      component="img"
      src={logoMark}
      alt=""
      sx={{ position: "absolute", left: 178, top: 92, width: 46 }}
    />
    <IconTile x={20} y={10} color={CLOUD_BLUE}>
      <SettingsIcon />
    </IconTile>
    <IconTile x={324} y={10} color={CLOUD_BLUE}>
      <GroupIcon />
    </IconTile>
    <IconTile x={10} y={134} color={CLOUD_BLUE}>
      <BarChartIcon />
    </IconTile>
    <IconTile x={334} y={134} color={CLOUD_BLUE}>
      <VerifiedUserIcon />
    </IconTile>
  </ArtCanvas>
);

// "</>" brackets with a slash.
const CodeSlashIcon = () => (
  <svg
    width="1.25em"
    height="1em"
    viewBox="0 0 30 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 7 L2 12 L7 17" />
    <path d="M23 7 L28 12 L23 17" />
    <path d="M17 5 L13 19" />
  </svg>
);

// A solid cylinder in three stacked bands.
const DatabaseIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor">
    <ellipse cx="12" cy="5.5" rx="8" ry="3" />
    <path d="M4 7.6 C5.6 9 8.6 9.7 12 9.7 S18.4 9 20 7.6 V11 C20 12.7 16.4 14 12 14 S4 12.7 4 11 Z" />
    <path d="M4 13.6 C5.6 15 8.6 15.7 12 15.7 S18.4 15 20 13.6 V18.5 C20 20.2 16.4 21.5 12 21.5 S4 20.2 4 18.5 Z" />
  </svg>
);

// Three nodes joined in a triangle: Conductor's workflow graph.
const WorkflowGraphIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 48 48" fill="currentColor">
    <path
      d="M24 13 L13 32 H35 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <circle cx="24" cy="13" r="7.5" />
    <circle cx="13" cy="32" r="7.5" />
    <circle cx="35" cy="32" r="7.5" />
  </svg>
);

const ConductorArt = () => (
  <ArtCanvas>
    <svg
      width={ART_WIDTH}
      height={ART_HEIGHT}
      style={{ position: "absolute", inset: 0 }}
    >
      <defs>
        <marker
          id="docs-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M0 0 L10 5 L0 10 z" fill="#8f7cf2" />
        </marker>
      </defs>
      <g
        fill="none"
        stroke="#8f7cf2"
        strokeWidth="2"
        markerEnd="url(#docs-arrow)"
      >
        <path d="M82 100 H110" />
        <path d="M168 56 V30 Q168 24 174 24 H236" />
        <path d="M168 144 V170 Q168 176 174 176 H236" />
      </g>
    </svg>
    <IconTile x={20} y={72} color={CONDUCTOR_PURPLE}>
      <CodeSlashIcon />
    </IconTile>
    <IconTile x={116} y={56} size={88} color={CONDUCTOR_PURPLE} filled>
      <WorkflowGraphIcon />
    </IconTile>
    <IconTile x={244} y={-4} color={CONDUCTOR_PURPLE}>
      <DatabaseIcon />
    </IconTile>
    <IconTile x={244} y={148} color={CONDUCTOR_PURPLE}>
      <DescriptionIcon />
    </IconTile>
  </ArtCanvas>
);

interface DocsCardProps {
  art: ReactNode;
  artBackground: string;
  title: string;
  description: string;
  action: ReactNode;
}

const DocsCard = ({
  art,
  artBackground,
  title,
  description,
  action,
}: DocsCardProps) => (
  <Box
    component="section"
    aria-label={title}
    sx={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: 3,
      overflow: "hidden",
      backgroundColor: "#fff",
      border: `1px solid ${DOCS_BORDER}`,
      boxShadow: "0 8px 24px rgba(30, 40, 90, 0.06)",
    }}
  >
    <Box
      sx={{
        height: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: artBackground,
        overflow: "hidden",
      }}
    >
      {art}
    </Box>
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        px: { xs: 3, md: 4 },
        py: { xs: 3, md: 3.5 },
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        sx={{ color: DOCS_HEADING, mb: 1 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{ flex: 1, lineHeight: 1.6, color: DOCS_TEXT, mb: 3 }}
      >
        {description}
      </Typography>
      {action}
    </Box>
  </Box>
);

const buttonSx = {
  textTransform: "none",
  minHeight: 44,
} as const;

export const DocsLanding = () => (
  <Box
    sx={{
      position: "relative",
      minHeight: "100%",
      overflow: "hidden",
      backgroundColor: "#f6f7fb",
    }}
  >
    {/* Soft wave shapes behind the content; the cards cover the middle, so they show at the edges. */}
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 1000 620"
      preserveAspectRatio="none"
      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      <path d="M0 0 H180 C150 70 90 115 0 130 Z" fill="#ebe7fb" />
      <path
        d="M1000 30 C910 70 860 150 880 230 C895 290 950 320 1000 330 Z"
        fill="#e2e9fb"
      />
      <path d="M0 360 C90 400 140 500 130 620 H0 Z" fill="#e9eefb" />
    </Box>
    {/* Decorative dot grids in the top-left and bottom-right corners. */}
    <Box
      aria-hidden
      sx={{
        ...DOT_GRID,
        position: "absolute",
        top: 0,
        left: "12%",
        width: 140,
        height: 120,
        display: { xs: "none", md: "block" },
      }}
    />
    <Box
      aria-hidden
      sx={{
        ...DOT_GRID,
        position: "absolute",
        bottom: 0,
        right: 24,
        width: 200,
        height: 90,
        display: { xs: "none", md: "block" },
      }}
    />
    <Box
      sx={{
        position: "relative",
        px: { xs: 3, md: 6 },
        pt: { xs: 6, md: 16 },
        pb: { xs: 4, md: 6 },
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            letterSpacing: "0.12em",
            color: DOCS_MUTED,
            mb: 1,
          }}
        >
          DOCUMENTATION
        </Typography>
        <Typography variant="h1" sx={{ color: DOCS_HEADING, mb: 1.5 }}>
          Hi, how can we help?
        </Typography>
        <Typography variant="body1" sx={{ color: DOCS_MUTED }}>
          Explore documentation for managing Orkes Cloud or building with Orkes
          Conductor.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 3, md: 5 },
        }}
      >
        <DocsCard
          art={<CloudArt />}
          artBackground="linear-gradient(180deg, #eef1fc 0%, #f4f5fd 100%)"
          title="Orkes Cloud Docs"
          description="Set up and manage your Orkes Cloud environment, including clusters, users, networking, SSO, monitoring, and upgrades."
          action={
            <Button
              variant="contained"
              disableElevation
              component={Link}
              to={docPath(allDocs[0].slug)}
              endIcon={<ArrowForwardIcon />}
              sx={{
                ...buttonSx,
                backgroundColor: CLOUD_BLUE,
                "&:hover": { backgroundColor: CLOUD_BLUE_DARK },
              }}
            >
              View Cloud Docs
            </Button>
          }
        />
        <DocsCard
          art={<ConductorArt />}
          artBackground="linear-gradient(135deg, #f1effd 0%, #ece8fc 100%)"
          title="Orkes Conductor Docs"
          description="Build and run workflows with Orkes Conductor. Explore concepts, developer guides, task references, integrations, SDKs, and APIs."
          action={
            <Button
              variant="outlined"
              href={DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                ...buttonSx,
                color: CLOUD_BLUE,
                borderColor: CLOUD_BLUE,
                borderWidth: 2,
                "&:hover": { borderWidth: 2, borderColor: CLOUD_BLUE_DARK },
              }}
            >
              View Conductor Docs
            </Button>
          }
        />
      </Box>
    </Box>
  </Box>
);
